import React, { useMemo } from 'react'
import { Drawer } from 'antd'
import dayjs from 'dayjs'

import {
  TransactionEvent,
  useTransactionEventsQuery,
} from '../../api/transaction-details-api-client'
import { DASH, formatMoney, formatMonth } from './helpers'
// Reuse the Benefits-tab activity drawer's visual language so both history
// surfaces read identically.
import styles from '../ReconciliationV2/ActivityLogDrawer.module.scss'

// ── design constants (mirrors ActivityLogDrawer) ────────────────────────────
const DRAWER_WIDTH = 440
const DRAWER_BORDER = '#E1E5E9'
const DRAWER_SHADOW = '-14px 0 30px -18px rgba(0,0,0,0.28)'

const CloseIcon: React.FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ width: 18, height: 18 }}
  >
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </svg>
)

const ClockIcon: React.FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.6}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ width: 34, height: 34, color: '#C3CDD6' }}
  >
    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
    <path d="M3 3v5h5" />
    <path d="M12 7v5l4 2" />
  </svg>
)

// ── event → view-model mapping ──────────────────────────────────────────────
const DISPLAY: Record<string, { label: string; tone: string }> = {
  ALLOCATED: { label: 'Allocated', tone: styles.toneBlue },
  DEALLOCATED: { label: 'Removed', tone: styles.toneDanger },
  ABSORPTION_RECORDED: { label: 'Absorbed', tone: styles.toneWarning },
  ABSORPTION_DELETED: { label: 'Absorption removed', tone: styles.toneMuted },
  RETURNED: { label: 'Returned', tone: styles.toneDanger },
  UNRETURNED: { label: 'Un-returned', tone: styles.toneMuted },
}

interface Entry {
  id: string
  label: string
  tone: string
  monthLabel: string | null
  when: string
  desc: string
  by: string
  amountFmt: string | null
}

const toEntry = (event: TransactionEvent): Entry => {
  const display = DISPLAY[event.eventType] ?? {
    label: event.eventType,
    tone: styles.toneMuted,
  }
  // amountCents may serialize as a number OR a bigint string (matching the
  // ledger wire types) — coerce both, ignore anything else.
  const rawCents = event.payload.amountCents
  const cents =
    typeof rawCents === 'number'
      ? rawCents
      : typeof rawCents === 'string'
        ? Number(rawCents)
        : NaN
  const period = event.payload.periodStartDate
  const absorptionType = event.payload.absorptionType
  const typeSuffix =
    typeof absorptionType === 'string'
      ? absorptionType === 'FULL'
        ? 'Full'
        : 'Partial'
      : null
  const rawNote = event.payload.note ?? event.payload.reason
  // UNRETURNED carries the reversed return's reason, not a reason of its own.
  const returnNote = event.payload.returnNote
  const note =
    typeof rawNote === 'string' && rawNote.length > 0
      ? rawNote
      : typeof returnNote === 'string' && returnNote.length > 0
        ? `Return reason: ${returnNote}`
        : null
  return {
    id: event.id,
    label: display.label,
    tone: display.tone,
    monthLabel: typeof period === 'string' ? formatMonth(period) : null,
    when: dayjs(event.createdAt).format('MMM D, YYYY · h:mm A'),
    desc: note ?? (typeSuffix ? `${typeSuffix} write-off` : display.label),
    by: event.createdBy ?? DASH,
    amountFmt: Number.isFinite(cents) ? formatMoney(cents / 100) : null,
  }
}

export interface TransactionEventDrawerProps {
  open: boolean
  onClose: () => void
  transactionId: string | null
  /** Short reference shown in the drawer subheader (e.g. the transaction id). */
  label?: string
}

/**
 * Right-side event-history drawer for one transaction — the ledger analog of the
 * Benefits tab's ActivityLogDrawer, over GET /transactions/:id/events. Events
 * are newest-first (the endpoint returns oldest-first).
 */
const TransactionEventDrawer: React.FC<TransactionEventDrawerProps> = ({
  open,
  onClose,
  transactionId,
  label,
}) => {
  const { data, isLoading } = useTransactionEventsQuery(
    open ? transactionId : null
  )
  const entries = useMemo(
    () => [...(data ?? [])].reverse().map(toEntry),
    [data]
  )

  const title = (
    <div className={styles.headerRow}>
      <div>
        <div className={styles.headerTitle}>Event history</div>
        {label && <div className={styles.headerSub}>{label}</div>}
      </div>
      <button
        type="button"
        aria-label="Close"
        className={styles.closeBtn}
        onClick={onClose}
      >
        <CloseIcon />
      </button>
    </div>
  )

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="right"
      width={DRAWER_WIDTH}
      title={title}
      closable={false}
      styles={{
        wrapper: { boxShadow: DRAWER_SHADOW },
        content: { borderLeft: `1px solid ${DRAWER_BORDER}` },
        header: { padding: '22px 24px 18px' },
        body: { padding: 0, display: 'flex', flexDirection: 'column' },
      }}
    >
      <div className={styles.list}>
        {isLoading && (
          <span className={styles.emptyText}>Loading history…</span>
        )}

        {!isLoading && entries.length === 0 && (
          <div className={styles.emptyWrap}>
            <ClockIcon />
            <span className={styles.emptyText}>No events yet.</span>
          </div>
        )}

        {entries.map((entry) => (
          <div className={styles.entryCard} key={entry.id}>
            <div className={styles.entryTopRow}>
              <span className={`${styles.overline} ${entry.tone}`}>
                {entry.label}
                {entry.monthLabel ? ` · ${entry.monthLabel}` : ''}
              </span>
              <span className={styles.when}>{entry.when}</span>
            </div>
            <div className={styles.desc}>{entry.desc}</div>
            <div className={styles.entryBottomRow}>
              <span className={styles.byLabel}>{entry.by}</span>
              {entry.amountFmt && (
                <span className={styles.amount}>{entry.amountFmt}</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </Drawer>
  )
}

export default TransactionEventDrawer
